import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogFooter,
  Input,
  Label,
} from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import {
  useServiceOrders,
  useServiceOrder,
  useServiceOrderActions,
  type OsPrioridade,
} from '@/lib/service-orders-store';
import {
  useQuestionarioTemplates,
  useQuestionarioPerguntas,
  useSubmitRespostas,
} from '@/lib/questionarios-store';
import { isPerguntaVisivel } from './pergunta-visivel';
import { PerguntaTecnicoInput } from './pergunta-tecnico';
import { useClient } from '@/lib/clients-store';
import { SignatureCanvas } from '@/components/SignatureCanvas';
import { formatRelativeTime } from '@/lib/format-activity';

import { formatDataHoraSegundos } from '@/lib/format-date';
/**
 * Modo Técnico (`/tecnico`) — UI minimalista mobile-first (R5).
 *
 * 4 tabs no rodapé: Minhas / Hoje / Eu (mapa fica como tech-debt — Leaflet
 * pesado pra mobile, validação Matheus primeiro)
 *
 * Auto-detecção de modo: hoje quem acessa /tecnico vê. Redirect baseado
 * em role (técnico → /tecnico ao logar) fica como tech-debt — Matheus
 * pode liberar acesso manual via menu.
 */
type Tab = 'minhas' | 'hoje' | 'eu';

export function TecnicoPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState<Tab>('minhas');
  const [openOsId, setOpenOsId] = useState<string | null>(null);

  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-paper-warm dark:bg-paper-dark-warm">
      {/* Topbar minimal */}
      <header className="flex items-center justify-between px-4 py-3 bg-paper-surface dark:bg-paper-dark-surface border-b border-line dark:border-line-dark">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-red text-white font-display italic flex items-center justify-center text-sm">
            i
          </span>
          <h1 className="font-display text-xl text-ink-1 dark:text-ink-dark-1">IGM</h1>
        </div>
        <Link
          to="/"
          className="text-xs text-ink-3 dark:text-ink-dark-3 hover:text-red"
          title="Voltar pro Modo Gestor"
        >
          ⇆ Gestor
        </Link>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto p-4 pb-20" data-testid={`tecnico-tab-${tab}`}>
        {tab === 'minhas' && (
          <MinhasOsList userId={user.id} onOpenOs={(id) => setOpenOsId(id)} />
        )}
        {tab === 'hoje' && <DashboardHoje userId={user.id} />}
        {tab === 'eu' && <PerfilTecnico userId={user.id} onLogout={logout} />}
      </main>

      {/* Tab bar fixa */}
      <nav
        className="fixed bottom-0 left-0 right-0 grid grid-cols-3 bg-paper-surface dark:bg-paper-dark-surface border-t border-line dark:border-line-dark z-10"
        data-testid="tecnico-tabbar"
      >
        <TabButton active={tab === 'minhas'} onClick={() => setTab('minhas')} icon="📋" label="Minhas" />
        <TabButton active={tab === 'hoje'} onClick={() => setTab('hoje')} icon="📊" label="Hoje" />
        <TabButton active={tab === 'eu'} onClick={() => setTab('eu')} icon="👤" label="Eu" />
      </nav>

      {openOsId && <OsExecucaoModal osId={openOsId} onClose={() => setOpenOsId(null)} />}

    </div>
  );
}

function Navigate({ to }: { to: string }) {
  useEffect(() => {
    window.location.href = to;
  }, [to]);
  return null;
}

interface TabButtonProps {
  active: boolean;
  icon: string;
  label: string;
  onClick: () => void;
}

function TabButton({ active, icon, label, onClick }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`tecnico-tab-btn-${label.toLowerCase()}`}
      className={`flex flex-col items-center py-2 text-xs transition-colors ${
        active
          ? 'text-red border-t-2 border-red bg-red-soft dark:bg-red-soft-dark'
          : 'text-ink-3 hover:text-red'
      }`}
    >
      <span className="text-lg leading-none">{icon}</span>
      <span className="mt-0.5">{label}</span>
    </button>
  );
}

// ─── Tab: Minhas OSs ────────────────────────────────────────────────────

const PRIORIDADE_LABELS: Record<OsPrioridade, string> = {
  baixa: 'Baixa',
  normal: 'Normal',
  alta: 'Alta',
  urgente: 'Urgente',
};

const STATUS_LABELS: Record<string, string> = {
  aberta: 'Aberta',
  atribuida: 'Atribuída',
  em_campo: 'Em campo',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
};

interface MinhasOsListProps {
  userId: string;
  onOpenOs: (id: string) => void;
}

function MinhasOsList({ userId, onOpenOs }: MinhasOsListProps) {
  const { data: oss = [], isLoading } = useServiceOrders({ tecnicoId: userId });

  // Ordena: urgentes primeiro, em_campo > atribuida > aberta > concluida/cancelada
  const ordered = [...oss].sort((a, b) => {
    const prio: Record<string, number> = { urgente: 0, alta: 1, normal: 2, baixa: 3 };
    const status: Record<string, number> = {
      em_campo: 0,
      atribuida: 1,
      aberta: 2,
      concluida: 3,
      cancelada: 4,
    };
    const sDiff = (status[a.status] ?? 99) - (status[b.status] ?? 99);
    if (sDiff !== 0) return sDiff;
    return (prio[a.prioridade] ?? 99) - (prio[b.prioridade] ?? 99);
  });

  if (isLoading) {
    return <div className="text-sm text-ink-3 italic text-center py-8">Carregando OSs…</div>;
  }
  if (oss.length === 0) {
    return (
      <EmptyState
        title="Sem OSs atribuídas"
        description="Quando um gestor te atribuir uma OS, ela aparece aqui."
      />
    );
  }

  return (
    <ul className="space-y-3">
      {ordered.map((os) => (
        <li key={os.id}>
          <button
            type="button"
            onClick={() => onOpenOs(os.id)}
            className="block w-full text-left"
            data-testid={`tecnico-os-${os.id}`}
          >
            <Card>
              <div className="p-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <Badge
                    variant={
                      os.status === 'em_campo' ? 'warning' : os.status === 'concluida' ? 'success' : 'neutral'
                    }
                  >
                    {STATUS_LABELS[os.status] ?? os.status}
                  </Badge>
                  {os.prioridade !== 'normal' && (
                    <Badge variant={os.prioridade === 'urgente' ? 'danger' : 'warning'}>
                      {PRIORIDADE_LABELS[os.prioridade]}
                    </Badge>
                  )}
                </div>
                <h3 className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 mb-1">
                  {os.titulo}
                </h3>
                <p className="text-xs text-ink-3 dark:text-ink-dark-3 truncate">{os.endereco}</p>
                {os.agendadoParaIso && (
                  <p className="text-xs text-ink-3 mt-1">
                    📅 {formatDataHoraSegundos(os.agendadoParaIso)}
                  </p>
                )}
              </div>
            </Card>
          </button>
        </li>
      ))}
    </ul>
  );
}

// ─── Tab: Hoje (dashboard) ─────────────────────────────────────────────

function DashboardHoje({ userId }: { userId: string }) {
  const { data: oss = [] } = useServiceOrders({ tecnicoId: userId });
  const hoje = new Date().toDateString();
  const concluidasHoje = oss.filter(
    (os) => os.fimAtendimentoIso && new Date(os.fimAtendimentoIso).toDateString() === hoje
  );
  const emAndamento = oss.filter((os) => os.status === 'em_campo');
  const proximas = oss
    .filter((os) => os.status === 'atribuida')
    .sort((a, b) => {
      const aDate = a.agendadoParaIso ? Date.parse(a.agendadoParaIso) : Number.MAX_SAFE_INTEGER;
      const bDate = b.agendadoParaIso ? Date.parse(b.agendadoParaIso) : Number.MAX_SAFE_INTEGER;
      return aDate - bDate;
    });

  return (
    <div className="space-y-4">
      <h2 className="font-display text-xl text-ink-1 dark:text-ink-dark-1">Hoje</h2>
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <div className="p-3 text-center">
            <p className="text-2xl font-display text-ink-1">{concluidasHoje.length}</p>
            <p className="text-xs text-ink-3 uppercase tracking-wider">OSs concluídas</p>
          </div>
        </Card>
        <Card>
          <div className="p-3 text-center">
            <p className="text-2xl font-display text-ink-1">{emAndamento.length}</p>
            <p className="text-xs text-ink-3 uppercase tracking-wider">Em andamento</p>
          </div>
        </Card>
      </div>
      <div>
        <h3 className="text-xs uppercase tracking-wider text-ink-3 mb-2">Próximas</h3>
        {proximas.length === 0 ? (
          <p className="text-xs text-ink-3 italic">Sem OSs agendadas</p>
        ) : (
          <ul className="space-y-2">
            {proximas.slice(0, 3).map((os) => (
              <li key={os.id} className="text-xs text-ink-2 dark:text-ink-dark-2">
                • {os.titulo}{' '}
                <span className="text-ink-3">
                  ({os.agendadoParaIso ? formatDataHoraSegundos(os.agendadoParaIso) : 'sem data'})
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Eu (perfil) ──────────────────────────────────────────────────

function PerfilTecnico({ userId, onLogout }: { userId: string; onLogout: () => void }) {
  return (
    <div className="space-y-4">
      <h2 className="font-display text-xl text-ink-1 dark:text-ink-dark-1">Meu Perfil</h2>
      <Card>
        <div className="p-4 space-y-2">
          <p className="text-xs text-ink-3">ID</p>
          <p className="text-sm font-mono text-ink-2">{userId}</p>
        </div>
      </Card>
      <Link
        to="/perfil"
        className="block text-sm text-red hover:text-red-deep underline"
      >
        Configurações pessoais (Modo Gestor) →
      </Link>
      <Button variant="ghost" onClick={onLogout} className="w-full">
        Sair
      </Button>
    </div>
  );
}

// ─── Modal de execução da OS ───────────────────────────────────────────

interface OsExecucaoModalProps {
  osId: string;
  onClose: () => void;
}

function OsExecucaoModal({ osId, onClose }: OsExecucaoModalProps) {
  const { data: os } = useServiceOrder(osId);
  const { changeStatus, submitSignature, pending } = useServiceOrderActions();
  const { data: cliente } = useClient(os?.clienteId ?? null);
  const { data: templates = [] } = useQuestionarioTemplates({
    workflowId: os?.workflowId ?? null,
    enabled: !!os?.workflowId,
  });
  const template = templates.find((t) => t.ativo && !t.deprecatedEmIso) ?? templates[0] ?? null;
  const { data: perguntas = [] } = useQuestionarioPerguntas(template?.id ?? null);
  const { submit: submitRespostas, pending: submitPending } = useSubmitRespostas();
  // Respostas indexadas por código da pergunta (P1, P2, ...)
  const [respostas, setRespostas] = useState<Record<string, unknown>>({});
  const [showSignature, setShowSignature] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signatureSvg, setSignatureSvg] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!os) return null;

  async function captureGpsAndAdvance(target: 'em_campo' | 'concluida') {
    setError(null);
    let coords: { latitude?: number; longitude?: number } = {};
    if (navigator.geolocation && target === 'em_campo') {
      // Tenta capturar GPS por até 5s, mas não bloqueia
      try {
        await new Promise<void>((resolve, reject) => {
          const t = setTimeout(() => reject(new Error('GPS timeout')), 5000);
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              clearTimeout(t);
              coords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
              resolve();
            },
            (err) => {
              clearTimeout(t);
              reject(err);
            },
            { enableHighAccuracy: true, timeout: 5000 }
          );
        });
      } catch {
        /* GPS opcional, segue sem */
      }
    }
    try {
      await changeStatus(osId, target, coords);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao mudar status');
    }
  }

  async function handleSubmitRespostas() {
    if (!template) {
      setError('Workflow não tem questionário associado');
      return;
    }
    const visiveis = perguntas.filter((p) => isPerguntaVisivel(p, respostas));
    const faltando = visiveis.filter((p) => p.obrigatoria && respostas[p.codigo] === undefined);
    if (faltando.length > 0) {
      setError(`Perguntas obrigatórias não respondidas: ${faltando.map((p) => p.codigo).join(', ')}`);
      return;
    }
    setError(null);
    try {
      await submitRespostas(osId, template.id, respostas);
      setRespostas({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro');
    }
  }

  async function handleConfirmSignature() {
    if (!signerName.trim() || !signatureSvg) {
      setError('Nome + assinatura obrigatórios');
      return;
    }
    setError(null);
    try {
      await submitSignature(osId, {
        signerName: signerName.trim(),
        signerRole: 'cliente',
        signatureSvg,
      });
      setShowSignature(false);
      setSignerName('');
      setSignatureSvg('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar assinatura');
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="md">
      <DialogHeader>
        <DialogTitle>{os.titulo}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        {error && (
          <Alert variant="danger" rail className="mb-3">
            {error}
          </Alert>
        )}
        <div className="space-y-4">
          <section>
            <div className="text-xs text-ink-3 uppercase tracking-wider mb-1">Cliente</div>
            <div className="text-sm text-ink-1">{cliente?.nome ?? os.clienteId}</div>
            <div className="text-xs text-ink-2 mt-0.5">{os.endereco}</div>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(os.endereco)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-red hover:underline mt-1 inline-block"
            >
              📍 Como chegar
            </a>
          </section>

          <section>
            <div className="text-xs text-ink-3 uppercase tracking-wider mb-2">Status atual</div>
            <Badge variant="info">{STATUS_LABELS[os.status]}</Badge>
            {os.inicioAtendimentoIso && (
              <div className="text-xs text-ink-3 mt-1">
                Check-in: {formatRelativeTime(os.inicioAtendimentoIso)}
              </div>
            )}
          </section>

          {/* Check-in */}
          {(os.status === 'aberta' || os.status === 'atribuida') && (
            <Button
              type="button"
              onClick={() => captureGpsAndAdvance('em_campo')}
              loading={pending === osId}
              className="w-full"
              data-testid="os-checkin"
            >
              🚀 Cheguei! (Check-in)
            </Button>
          )}

          {/* Questionário estruturado (Bloco B) */}
          {os.status === 'em_campo' && template && perguntas.length > 0 && (
            <section>
              <h4 className="text-sm font-medium text-ink-1 mb-2">
                {template.nome}{' '}
                <span className="text-xs text-ink-3">v{template.versao}</span>
              </h4>
              <div className="space-y-3">
                {perguntas
                  .filter((p) => isPerguntaVisivel(p, respostas))
                  .map((p) => (
                    <PerguntaTecnicoInput
                      key={p.id}
                      pergunta={p}
                      value={respostas[p.codigo]}
                      onChange={(v) =>
                        setRespostas((r) => ({ ...r, [p.codigo]: v }))
                      }
                    />
                  ))}
              </div>
              <Button
                type="button"
                size="sm"
                onClick={handleSubmitRespostas}
                loading={submitPending === osId}
                className="mt-3"
                data-testid="os-questionario-submit"
              >
                Salvar respostas
              </Button>
            </section>
          )}

          {/* Assinatura cliente */}
          {os.status === 'em_campo' && !os.clienteAprovou && (
            <section>
              {showSignature ? (
                <div className="space-y-2">
                  <Label htmlFor="signer-name" required>
                    Nome do cliente
                  </Label>
                  <Input
                    id="signer-name"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="Quem está assinando"
                  />
                  <Label>Assinatura</Label>
                  <SignatureCanvas onChange={setSignatureSvg} />
                  <Button
                    type="button"
                    onClick={handleConfirmSignature}
                    disabled={!signerName || !signatureSvg}
                    className="w-full mt-2"
                  >
                    Confirmar assinatura cliente
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowSignature(true)}
                  className="w-full"
                  data-testid="os-sig-start"
                >
                  ✍️ Colher assinatura do cliente
                </Button>
              )}
            </section>
          )}

          {os.clienteAprovou && (
            <Alert variant="success" rail>
              ✓ Cliente aprovou a OS
            </Alert>
          )}

          {/* Check-out */}
          {os.status === 'em_campo' && (
            <Button
              type="button"
              variant={os.clienteAprovou ? 'primary' : 'ghost'}
              onClick={() => captureGpsAndAdvance('concluida')}
              loading={pending === osId}
              className="w-full"
              data-testid="os-checkout"
            >
              ✓ Finalizar OS
            </Button>
          )}
        </div>
      </DialogContent>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Fechar
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
